import { useEffect, useState } from "react";

interface TypingEffectProps {
  /**
   * Array of text items to cycle through
   */
  items?: string[];
  /**
   * Speed of typing in milliseconds
   */
  typingSpeed?: number;
  /**
   * Speed of deleting in milliseconds
   */
  deletingSpeed?: number;
  /**
   * Delay after typing completes before starting to delete
   */
  delayAfterType?: number;
  /**
   * Whether to show the blinking cursor
   */
  showCursor?: boolean;
  /**
   * Character to use as cursor
   */
  cursorChar?: string;
}

/**
 * TypingEffect component that cycles through an array of text items with a typing animation
 */
export function TypingEffect({
  items = [],
  typingSpeed = 150,
  deletingSpeed = 75,
  delayAfterType = 1500,
  showCursor = true,
  cursorChar = "|",
}: TypingEffectProps) {
  const [displayText, setDisplayText] = useState<string>("");
  const [itemIndex, setItemIndex] = useState<number>(0);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [currentSpeed, setCurrentSpeed] = useState<number>(typingSpeed);
  const [isInDelayPhase, setIsInDelayPhase] = useState<boolean>(false);

  useEffect(() => {
    // Don't do anything if the items array is empty
    if (items.length === 0) return;

    let timer;

    if (isDeleting) {
      // Deleting phase
      setIsInDelayPhase(false);
      timer = setTimeout(() => {
        setDisplayText((prev) => prev.substring(0, prev.length - 1));

        // When deletion is complete, move to next item
        if (displayText.length === 0) {
          setIsDeleting(false);
          setItemIndex((prev) => (prev + 1) % items.length);
          setCurrentSpeed(typingSpeed);
        }
      }, deletingSpeed);
    } else {
      // Typing phase
      const currentItem = items[itemIndex];

      if (displayText.length < currentItem.length) {
        // Continue typing
        setIsInDelayPhase(false);
        timer = setTimeout(() => {
          setDisplayText(currentItem.substring(0, displayText.length + 1));
        }, currentSpeed);
      } else {
        // Finished typing, wait and then start deleting
        setIsInDelayPhase(true);
        timer = setTimeout(() => {
          setIsInDelayPhase(false);
          setIsDeleting(true);
        }, delayAfterType);
      }
    }

    return () => clearTimeout(timer);
  }, [
    displayText,
    itemIndex,
    isDeleting,
    items,
    currentSpeed,
    typingSpeed,
    deletingSpeed,
    delayAfterType,
    isInDelayPhase,
  ]);

  return (
    <span>
      {displayText}
      {showCursor && (
        <span
          className={`inline-block ${isInDelayPhase ? "animate-pulse" : ""}`}
        >
          {cursorChar}
        </span>
      )}
    </span>
  );
}
